import { useEffect, useMemo, useState } from "react";
import Corporate from "./Corporate";
import faq from "./content/faq.json";
import legal from "./content/legal.json";
import { useI18n } from "./i18n";

const newsModules = import.meta.glob("./content/news/*.json", {
  eager: true,
  import: "default",
});

const newsPosts = Object.entries(newsModules)
  .map(([path, post]) => ({
    ...post,
    slug: path.split("/").pop().replace(".json", ""),
  }))
  .sort((a, b) => new Date(b.date) - new Date(a.date));

const NEWS_IMAGE_VERSION = {
  "/images/news/sbjj-autumn-open-bastida-2026.webp": "20260816",
};

function newsImageSrc(src) {
  const version = NEWS_IMAGE_VERSION[src];
  return version ? `${src}?v=${version}` : src;
}

const DAYS = [
  ["monday", "Mon"],
  ["tuesday", "Tue"],
  ["wednesday", "Wed"],
  ["thursday", "Thu"],
  ["friday", "Fri"],
  ["saturday", "Sat"],
  ["sunday", "Sun"],
];

const AUDIENCES = [
  ["all", "All classes"],
  ["adults", "Adults"],
  ["kids", "Kids"],
  ["women", "Women"],
  ["mma", "MMA"],
];

const audienceLabel = {
  adults: "Adults",
  kids: "Kids",
  women: "Women",
  mma: "MMA",
  open: "Open",
};

const AUTUMN_HOLIDAY_START = "2026-10-05";
const AUTUMN_HOLIDAY_END = "2026-10-18";
const AUTUMN_HOLIDAY_ANNOUNCE_START = "2026-10-04";

const HOLIDAY_COPY = {
  de: {
    announcement: "Schulferien · Spezieller Stundenplan 5.–18. Oktober",
    announcementCta: "Zeiten ansehen",
    zurichUpcoming: "Schulferien vom 5.–18. Oktober: In Zürich gilt ein spezieller Ferien-Stundenplan. 5.–11.10.: Montag, Mittwoch und Freitag regulär; Dienstag und Donnerstag nur Erwachsene; Samstag geschlossen. 12.–18.10.: Kids Camp Mo–Fr 09:00–12:00, keine regulären Lektionen während des Camps; übrige Erwachsenenklassen finden statt.",
    zurichWeek1: "Ferien-Stundenplan 5.–11. Oktober: Montag, Mittwoch und Freitag regulär. Dienstag und Donnerstag nur Erwachsene – keine Kids Classes. Samstag geschlossen. Sonntag regulär.",
    zurichWeek2: "Ferien-Stundenplan 12.–18. Oktober: Kids Herbstcamp Montag bis Freitag 09:00–12:00. Währenddessen keine regulären Lektionen (Freitag 10:00 entfällt). Die übrigen Erwachsenenklassen finden statt; reguläre Kids Classes entfallen.",
    silverUpcoming: "Schulferien vom 5.–18. Oktober: Im Silver Coast Dojo gilt ein spezieller Ferien-Stundenplan. 5.–8.10.: Kids 17:00–18:00, Adults 18:00–19:00. 9.–10.10. geschlossen. 12.–18.10. vollständig geschlossen.",
    silverWeek1: "Ferien-Stundenplan 5.–11. Oktober: Montag bis Donnerstag Kids 17:00–18:00 und Adults 18:00–19:00. Freitag und Samstag geschlossen.",
    silverWeek2: "Ferien-Stundenplan 12.–18. Oktober: Das Silver Coast Dojo ist geschlossen. Keine Klassen.",
    cancelled: "Entfällt während Schulferien",
    special: "Ferienzeit",
    closed: "Dojo geschlossen",
    closedCopy: "An diesem Tag finden während der Schulferien keine Klassen statt.",
  },
  en: {
    announcement: "School holidays · Special schedule 5–18 October",
    announcementCta: "View schedule",
    zurichUpcoming: "School holidays from 5–18 October: a special holiday schedule applies in Zurich. 5–11 Oct: regular schedule on Monday, Wednesday and Friday; adults only on Tuesday and Thursday; closed Saturday. 12–18 Oct: Kids Camp Mon–Fri 09:00–12:00, no regular classes during camp; other adult classes continue.",
    zurichWeek1: "Holiday schedule 5–11 October: regular schedule on Monday, Wednesday and Friday. Adults only on Tuesday and Thursday – no Kids Classes. Closed Saturday. Regular schedule Sunday.",
    zurichWeek2: "Holiday schedule 12–18 October: Kids Autumn Camp Monday to Friday 09:00–12:00. No regular classes during camp hours (Friday 10:00 cancelled). Other adult classes continue; regular kids classes are cancelled.",
    silverUpcoming: "School holidays from 5–18 October: a special holiday schedule applies at Silver Coast. 5–8 Oct: Kids 17:00–18:00, Adults 18:00–19:00. Closed 9–10 Oct. Fully closed 12–18 Oct.",
    silverWeek1: "Holiday schedule 5–11 October: Monday to Thursday Kids 17:00–18:00 and Adults 18:00–19:00. Closed Friday and Saturday.",
    silverWeek2: "Holiday schedule 12–18 October: Silver Coast Dojo is closed. No classes.",
    cancelled: "Cancelled during school holidays",
    special: "Holiday schedule",
    closed: "Dojo closed",
    closedCopy: "No classes take place on this day during the school holidays.",
  },
};

function holidayPhase(dateKey) {
  if (dateKey < AUTUMN_HOLIDAY_START || dateKey > AUTUMN_HOLIDAY_END) return null;
  return dateKey <= "2026-10-11" ? "week1" : "week2";
}


function matchesAudience(item, audience) {
  if (audience === "all" || item.audience === audience) return true;
  return audience === "mma"
    && item.audience === "adults"
    && /MMA|Striking/i.test(item.format || "");
}

function Arrow({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InstagramIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function PinIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function MenuIcon({ open }) {
  return (
    <span className={`menu-icon ${open ? "is-open" : ""}`} aria-hidden="true">
      <span />
      <span />
    </span>
  );
}

function Logo({ compact = false, homeHref = "#top" }) {
  return (
    <a className={`logo ${compact ? "logo--compact" : ""}`} href={homeHref} aria-label="Tiago de Paula BJJ home">
      <span className="logo__mark">
        <img src="/images/logo.svg" alt="" />
      </span>
      <span className="logo__type">
        <strong>TIAGO DE PAULA</strong>
        <small>BRAZILIAN JIU-JITSU</small>
      </span>
    </a>
  );
}

function SectionIntro({ eyebrow, title, copy, light = false, action }) {
  return (
    <div className={`section-intro ${light ? "section-intro--light" : ""}`}>
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      {(copy || action) && (
        <div className="section-intro__aside">
          {copy && <p>{copy}</p>}
          {action}
        </div>
      )}
    </div>
  );
}

export function Header({ menuOpen, setMenuOpen, homeLinks = false, daypassEnabled = false }) {
  const { content: { site }, ui, language, setLanguage } = useI18n();
  const home = homeLinks ? `/${language}/` : "";
  const close = () => setMenuOpen(false);
  const dateKey = zurichDateKey();
  const holidayAnnouncementVisible = false; // Holiday banner hidden; standard announcement restored.
  const announcementText = holidayAnnouncementVisible ? HOLIDAY_COPY[language].announcement : site.announcement;
  const announcementLink = holidayAnnouncementVisible ? "#schedule" : site.announcement_link;
  return (
    <>
      <a className="skip-link" href="#main">{ui.skip}</a>
      <div className={holidayAnnouncementVisible ? "announcement announcement--holiday" : "announcement"}>
        <a href={announcementLink.startsWith("#") ? `${home}${announcementLink}` : announcementLink}>
          <span>{announcementText}</span>
          {holidayAnnouncementVisible && <strong>{HOLIDAY_COPY[language].announcementCta}</strong>}
          <Arrow size={15} />
        </a>
      </div>
      <header className="header">
        <Logo homeHref={homeLinks ? `/${language}/` : "#top"} />
        <nav className={menuOpen ? "nav is-open" : "nav"} aria-label="Main navigation">
          <a href={`${home}#programs`} onClick={close}>{ui.nav.programs}</a>
          <a href={`${home}#corporate`} onClick={close}>{language === "de" ? "Für Firmen" : "Corporate"}</a>
          <a href={`${home}#dojos`} onClick={close}>{ui.nav.dojos}</a>
          <a href={`${home}#schedule`} onClick={close}>{ui.nav.schedule}</a>
          <a href={`${home}#pricing`} onClick={close}>{ui.nav.pricing}</a>
          {daypassEnabled && <a href={`/${language}/daypass/`} onClick={close}>Day Pass</a>}
          {/* Direct links to the team and latest dojo updates. */}
          <a href={`${home}#coaches`} onClick={close}>{ui.nav.team}</a>
          <a href={`${home}#news`} onClick={close}>{ui.nav.news}</a>
          <a href={`${home}#contact`} onClick={close}>{ui.nav.contact}</a>
          <div className="language-switch" aria-label="Language">
            <button className={language === "de" ? "active" : ""} onClick={() => { setLanguage("de"); close(); }} aria-pressed={language === "de"}>DE</button>
            <span>/</span>
            <button className={language === "en" ? "active" : ""} onClick={() => { setLanguage("en"); close(); }} aria-pressed={language === "en"}>EN</button>
          </div>
          <a className="button button--small" href={`${home}#trial`} onClick={close}>{ui.nav.trial} <Arrow /></a>
        </nav>
        <button
          className="menu-button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <MenuIcon open={menuOpen} />
        </button>
      </header>
    </>
  );
}

function DaypassButton({ dojo, light = false }) {
  const { language } = useI18n();
  return <a className={`button ${light ? 'button--daypass-light' : ''}`} href={`/${language}/daypass/${dojo ? `?dojo=${dojo}` : ''}`}>
    {language === 'de' ? 'Als Gast trainieren · Day Pass CHF 35 · 18+' : 'Train as a guest · Day Pass CHF 35 · 18+'} <Arrow />
  </a>;
}

function Hero({ daypassEnabled }) {
  const { content: { site }, ui, language } = useI18n();
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__copy">
        <p className="eyebrow">{site.hero.eyebrow}</p>
        <h1 id="hero-title">
          {site.hero.title}
          <span>{site.hero.title_accent}</span>
        </h1>
        <p className="hero__intro">{site.hero.intro}</p>
        <div className="button-row">
          <a className="button" href="#trial">{ui.hero.trial} <Arrow /></a>
          <a className="text-link text-link--light" href="#schedule">{ui.hero.schedule} <Arrow /></a>
          {daypassEnabled && <DaypassButton light />}
        </div>
        <div className="hero__locations" aria-label="Our locations">
          <span><PinIcon /> Zürich</span>
          <span><PinIcon /> Thalwil</span>
        </div>
      </div>
      <div className="hero__visual">
        <img src={site.hero.image} alt={site.hero.image_alt} />
        <div className="hero__stamp"><strong>EST.</strong><span>2017</span><small>ZÜRICH</small></div>
      </div>
    </section>
  );
}

function Intro() {
  const { content: { site } } = useI18n();
  return (
    <section className="intro section-shell">
      <div className="intro__copy">
        <p className="eyebrow">{site.intro.eyebrow}</p>
        <h2>{site.intro.title}</h2>
        <p>{site.intro.body}</p>
      </div>
      <blockquote>{site.intro.quote}</blockquote>
      <div className="stats">
        {site.stats.map((stat) => (
          <div className="stat" key={stat.label}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Programs({ chooseAudience }) {
  const { content: { site }, ui } = useI18n();
  const handleChoice = (audience) => {
    chooseAudience(audience);
    document.querySelector("#schedule")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section className="programs section-pad" id="programs">
      <div className="section-shell">
        <SectionIntro
          eyebrow={ui.programs.eyebrow}
          title={ui.programs.title}
          copy={ui.programs.copy}
        />
        <div className="program-grid">
          {site.programs.map((program, index) => (
            <article className="program-card" key={program.title}>
              <div className="program-card__image">
                <img src={program.image} alt="" loading="lazy" />
                <span>0{index + 1}</span>
              </div>
              <div className="program-card__copy">
                <p className="card-tag">{program.tag}</p>
                <h3>{program.title}</h3>
                <p>{program.description}</p>
                <button className="text-link" onClick={() => handleChoice(program.audience)}>
                  {ui.programs.classes} <Arrow />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FirstVisit() {
  const { content: { site } } = useI18n();
  return (
    <section className="first-visit section-pad">
      <div className="section-shell">
        <SectionIntro
          eyebrow={site.first_visit.eyebrow}
          title={site.first_visit.title}
          copy={site.first_visit.intro}
        />
        <div className="first-visit__steps">
          {site.first_visit.steps.map((step) => (
            <article key={step.number}>
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Dojos({ chooseDojo, activeDojo, daypassEnabled }) {
  const { content: { site }, ui, language } = useI18n();
  const handleChoice = (id) => {
    chooseDojo(id);
    document.querySelector("#schedule")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section className="dojos section-pad" id="dojos">
      <div className="section-shell">
        <SectionIntro
          light
          eyebrow={ui.dojos.eyebrow}
          title={ui.dojos.title}
          copy={ui.dojos.copy}
        />
        <div className="dojo-grid">
          {site.dojos.map((dojo) => (
            <article className={`dojo-card ${activeDojo === dojo.id ? "dojo-card--active" : ""}`} key={dojo.id}>
              <img src={dojo.image} alt={`${dojo.name} training space`} loading="lazy" />
              <div className="dojo-card__overlay" />
              {activeDojo === dojo.id && dojo.partner_logo && (
                <div className="dojo-card__partner">
                  <img src={dojo.partner_logo} alt={dojo.partner_logo_alt || dojo.partner_name || "Partner logo"} loading="lazy" />
                </div>
              )}
              <div className="dojo-card__copy">
                <p className="eyebrow">{dojo.city}</p>
                <h3>{dojo.name}</h3>
                <p>{dojo.description}</p>
                <p className="dojo-card__detail">{dojo.detail}</p>
                <div className="dojo-card__links">
                  <button className="text-link text-link--light" onClick={() => handleChoice(dojo.id)}>{ui.dojos.schedule} <Arrow /></button>
                  {daypassEnabled && <DaypassButton dojo={dojo.id} light />}
                  <a href={dojo.map_url} target="_blank" rel="noreferrer"><PinIcon /> {dojo.address}</a>
                </div>
              </div>
            </article>
          ))}
        </div>
        {/* One shared rules document applies to both dojo locations. */}
        <div className="dojo-rules-action">
          <a className="button" href="/documents/dojo-rules.pdf" target="_blank" rel="noreferrer">
            {ui.dojos.rules} <Arrow />
          </a>
        </div>
      </div>
    </section>
  );
}

function Schedule({ dojo, setDojo, audience, setAudience, daypassEnabled }) {
  const { content: { site, zurichSchedule, silverSchedule }, ui, language } = useI18n();
  const [day, setDay] = useState(() => {
    const today = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "Europe/Zurich" })
      .format(new Date())
      .toLowerCase();
    return DAYS.some(([value]) => value === today) ? today : "monday";
  });

  const configuredSchedule = dojo === "zurich" ? zurichSchedule : silverSchedule;
  const today = zurichDateKey();
  const futureSchedule = configuredSchedule.future_schedule;
  const futureScheduleVisible = Boolean(futureSchedule && today >= futureSchedule.publish_date);
  const current = futureScheduleVisible
    ? { ...configuredSchedule, classes: futureSchedule.classes, notice: futureSchedule.notice }
    : configuredSchedule;
  const currentDojo = site.dojos.find((item) => item.id === dojo);
  const temporary = futureScheduleVisible ? null : current.temporary_schedule;
  const temporaryActive = Boolean(temporary && today >= temporary.start && today <= temporary.end);
  const kidsCampActive = Boolean(temporaryActive && today >= temporary.kids_camp_start && today <= temporary.kids_camp_end);
  const autumnHolidayPhase = holidayPhase(today);
  const holidayNoticeVisible = false; // Show the regular timetable only.
  const holidayDisplayPhase = holidayNoticeVisible
    ? (today <= "2026-10-11" ? "week1" : "week2")
    : null;

  const displayClasses = useMemo(() => {
    if (holidayDisplayPhase) {
      const regular = current.classes.map((item) => {
        let cancelled = false;
        if (dojo === "zurich") {
          cancelled = holidayDisplayPhase === "week2"
            ? (item.audience === "kids" || (item.day === "friday" && item.start === "10:00"))
            : item.day === "saturday" || ((item.day === "tuesday" || item.day === "thursday") && item.audience === "kids");
        } else if (holidayDisplayPhase === "week2") {
          cancelled = true;
        } else {
          const isHolidayTrainingDay = ["monday", "tuesday", "wednesday", "thursday"].includes(item.day);
          const isKidsHolidaySlot = item.start === "17:00" && item.audience === "kids";
          const isAdultsHolidaySlot = item.start === "18:00" && item.audience === "adults";
          const runsOnHolidaySchedule = isHolidayTrainingDay && (isKidsHolidaySlot || isAdultsHolidaySlot);
          cancelled = !runsOnHolidaySchedule;
          if (runsOnHolidaySchedule) {
            return { ...item, holidayStatus: "special" };
          }
        }
        return { ...item, holidayStatus: cancelled ? "cancelled" : "regular" };
      });

      if (dojo === "zurich" && holidayDisplayPhase === "week2") {
        const camp = ["monday", "tuesday", "wednesday", "thursday", "friday"].map((day) => ({
          day,
          start: "09:00",
          title: language === "de" ? "Kids Herbstcamp · 09:00–12:00" : "Kids Autumn Camp · 09:00–12:00",
          audience: "kids",
          format: "BJJ",
          level: language === "de" ? "Feriencamp · CHF 70/Tag oder CHF 250/Woche" : "Holiday camp · CHF 70/day or CHF 250/week",
          coach: "Team Tiago",
          holidayStatus: "special",
        }));
        return [...regular, ...camp];
      }
      return regular;
    }

    if (!temporaryActive) return current.classes;
    return current.classes.filter(
      (item) => temporary.days.includes(item.day) && !(kidsCampActive && item.audience === "kids"),
    );
  }, [current, dojo, temporary, temporaryActive, kidsCampActive, holidayDisplayPhase, language]);

  const holidayNotice = holidayNoticeVisible
    ? HOLIDAY_COPY[language][dojo === "zurich"
      ? (holidayDisplayPhase === "week1" ? "zurichWeek1" : "zurichWeek2")
      : (holidayDisplayPhase === "week1" ? "silverWeek1" : "silverWeek2")]
    : null;
  const scheduleNotice = null; // Suppress the outdated holiday and timetable notices.

  const dayClosed = Boolean(holidayDisplayPhase && (
    (dojo === "zurich" && holidayDisplayPhase === "week1" && day === "saturday")
    || (dojo === "silver" && holidayDisplayPhase === "week1" && (day === "friday" || day === "saturday"))
    || (dojo === "silver" && holidayDisplayPhase === "week2")
  ));

  const filteredClasses = useMemo(
    () => displayClasses
      .filter((item) => item.day === day && matchesAudience(item, audience))
      .sort((a, b) => a.start.localeCompare(b.start) || (a.holidayStatus === "special" ? -1 : 1)),
    [displayClasses, day, audience],
  );

  const dayCounts = useMemo(() => {
    const counts = {};
    DAYS.forEach(([value]) => {
      counts[value] = displayClasses.filter(
        (item) => item.day === value
          && item.holidayStatus !== "cancelled"
          && matchesAudience(item, audience),
      ).length;
    });
    return counts;
  }, [displayClasses, audience]);

  return (
    <section className="schedule section-pad" id="schedule">
      <div className="section-shell">
        <SectionIntro
          eyebrow={ui.schedule.eyebrow}
          title={ui.schedule.title}
          copy={ui.schedule.copy}
        />

        <div className="schedule__toolbar">
          <div className="segmented" aria-label="Choose dojo">
            <button className={dojo === "zurich" ? "active" : ""} onClick={() => setDojo("zurich")}>Zürich</button>
            <button className={dojo === "silver" ? "active" : ""} onClick={() => setDojo("silver")}>Thalwil</button>
          </div>
          <div className="filter-row" aria-label="Filter target group">
            {AUDIENCES.map(([value]) => (
              <button key={value} className={audience === value ? "active" : ""} onClick={() => setAudience(value)}>{ui.schedule[value]}</button>
            ))}
          </div>
          {currentDojo?.partner_logo && (
            <div className="schedule__partner">
              <span>{language === "de" ? "Partner des Silver Coast Dojos" : "Silver Coast dojo partner"}</span>
              <img src={currentDojo.partner_logo} alt={currentDojo.partner_logo_alt || currentDojo.partner_name || "Partner logo"} />
            </div>
          )}
        </div>

        {scheduleNotice && <div className="schedule__notice"><span>i</span>{scheduleNotice}</div>}

        <div className="day-tabs" role="tablist" aria-label="Choose day">
          {DAYS.map(([value]) => (
            <button
              key={value}
              className={day === value ? "active" : ""}
              onClick={() => setDay(value)}
              role="tab"
              aria-selected={day === value}
            >
              <span>{ui.days[value]}</span><small>{dayCounts[value]}</small>
              {value === new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "Europe/Zurich" }).format(new Date()).toLowerCase() && <em>{ui.schedule.today}</em>}
            </button>
          ))}
        </div>

        <div className="class-list" aria-live="polite">
          {dayClosed && (
            <div className="holiday-closed">
              <strong>{HOLIDAY_COPY[language].closed}</strong>
              <span>{HOLIDAY_COPY[language].closedCopy}</span>
            </div>
          )}
          {filteredClasses.length > 0 ? filteredClasses.map((item, index) => (
            <article
              className={`class-card class-card--${item.audience} ${item.holidayStatus ? `class-card--holiday-${item.holidayStatus}` : ""}`}
              key={`${item.day}-${item.start}-${item.title}-${item.holidayStatus || "normal"}-${index}`}
              aria-disabled={item.holidayStatus === "cancelled" ? "true" : undefined}
            >
              <time>{item.start}</time>
              <div className="class-card__main">
                <p className="class-card__meta">
                  <span>{ui.schedule[item.audience] || audienceLabel[item.audience] || item.audience}</span>
                  <span>{item.format}</span>
                  {/beginner|anfänger/i.test(item.level) && <span className="beginner-badge">{ui.schedule.beginner}</span>}
                  {item.holidayStatus === "special" && <span className="holiday-badge holiday-badge--special">{HOLIDAY_COPY[language].special}</span>}
                  {item.holidayStatus === "cancelled" && <span className="holiday-badge holiday-badge--cancelled">{HOLIDAY_COPY[language].cancelled}</span>}
                </p>
                <h3>{item.title}{item.audience === "kids" && !/competition|drills/i.test(item.title) && (
                  <> · {(() => {
                    const age = (language === "de" ? (item.level_de || item.level) : item.level)
                      .match(/(?:Ages? |ages? |Mädchen )?(\d+[–-]\d+)/);
                    return age
                      ? (language === "de" ? `${age[1]} Jahre` : `Ages ${age[1]}`)
                      : null;
                  })()}</>
                )}</h3>
              </div>
              <div className="class-card__details">
                <p><small>{ui.schedule.level}</small>{item.audience === "kids" && /competition|drills/i.test(item.title) ? (language === "de" ? "Teilnahme nach Auswahl durch Coaches" : "Participation by coach selection") : item.level}</p>
              </div>
              {item.holidayStatus === "cancelled" && (
                <div className="class-card__holiday-mask" aria-hidden="true">
                  <span>{HOLIDAY_COPY[language].cancelled}</span>
                </div>
              )}
            </article>
          )) : !dayClosed ? (
            <div className="empty-state">
              <strong>{ui.schedule.empty_title}</strong>
              <span>{ui.schedule.empty_copy}</span>
              <button className="text-link" onClick={() => setAudience("all")}>{ui.schedule.show_all} <Arrow /></button>
            </div>
          ) : null}
        </div>
        <p className="schedule__footnote">{ui.schedule.footnote}</p>
        {daypassEnabled && <DaypassButton dojo={dojo} />}
      </div>
    </section>
  );
}

function Coaches() {
  const { content: { site }, language } = useI18n();
  return (
    <section className="coaches section-pad" id="coaches">
      <div className="section-shell">
        <SectionIntro eyebrow={site.coaches_intro.eyebrow} title={site.coaches_intro.title} copy={site.coaches_intro.intro} />
        <div className="coaches-grid">
          {site.coaches.map((coach) => (
            <article className={`coach-card ${coach.featured ? "coach-card--featured" : ""}`} key={coach.name}>
              <div className="coach-card__visual">
                {coach.image ? (
                  <img src={coach.image} alt={coach.name} loading="lazy" />
                ) : (
                  <div className="coach-card__placeholder" role="img" aria-label={`${coach.name} portrait placeholder`}>
                    <span>{initials(coach.name)}</span>
                    <small>PHOTO</small>
                  </div>
                )}
                {coach.featured && <span className="coach-card__featured-label">{language === "de" ? "GRÜNDER · HEAD COACH" : "FOUNDER · HEAD COACH"}</span>}
              </div>
              <div className="coach-card__copy">
                <p className="coach-card__belt">{coach.belt}</p>
                <h3>{coach.name}</h3>
                <h4>{coach.roles}</h4>
                <p className="coach-card__bio">{coach.bio}</p>
                {coach.languages && <p className="coach-card__languages"><strong>{language === "de" ? "Sprachen" : "Languages"}</strong>{coach.languages}</p>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ daypassEnabled }) {
  const { content: { pricing }, ui } = useI18n();
  const [category, setCategory] = useState(pricing.categories[0]?.id || "kids");
  const currentCategory = pricing.categories.find((item) => item.id === category);
  const plans = pricing.plans.filter((plan) => plan.active !== false && plan.category === category);

  return (
    <section className="pricing section-pad" id="pricing">
      <div className="section-shell">
        <SectionIntro
          eyebrow={pricing.eyebrow}
          title={pricing.heading}
          copy={pricing.intro}
        />
        <div className="pricing-tabs" role="tablist" aria-label="Pricing category">
          {pricing.categories.map((item) => (
            <button
              key={item.id}
              className={category === item.id ? "active" : ""}
              onClick={() => setCategory(item.id)}
              role="tab"
              aria-selected={category === item.id}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="pricing-category">
          <p>{currentCategory?.description}</p>
          <span>{plans.length} {plans.length === 1 ? ui.pricing.option : ui.pricing.options}</span>
        </div>
        <div className="pricing-grid" aria-live="polite">
          {plans.map((plan, index) => (
            <article className={`price-card ${plan.featured ? "price-card--featured" : ""}`} key={`${plan.name}-${plan.duration}-${index}`}>
              {plan.featured && <span className="price-card__badge">{ui.pricing.best}</span>}
              <p className="price-card__duration">{plan.duration}</p>
              <h3>{plan.name}</h3>
              <p className="price-card__frequency">{plan.frequency}</p>
              <div className="price-card__price">
                <span>{plan.currency}</span>
                <strong>{Number(plan.price).toLocaleString("de-CH")}</strong>
              </div>
              <p className="price-card__description">{plan.description}</p>
              {annualSaving(plan, pricing.plans) > 0 && (
                <p className="price-card__saving">{ui.pricing.save} {plan.currency} {annualSaving(plan, pricing.plans).toLocaleString("de-CH")}</p>
              )}
              <a className="text-link" href={plan.checkout_url || "#trial"} target={plan.checkout_url ? "_blank" : undefined} rel={plan.checkout_url ? "noreferrer" : undefined}>
                {plan.checkout_url ? ui.pricing.choose : ui.pricing.ask} <Arrow />
              </a>
            </article>
          ))}
        </div>
        <p className="pricing__note">{pricing.note}</p>
        {daypassEnabled && <DaypassButton />}
      </div>
    </section>
  );
}

function VideoSection() {
  const { content: { video }, ui } = useI18n();
  const [playing, setPlaying] = useState(false);
  return (
    <section className="video-section section-pad">
      <div className="section-shell video-section__grid">
        <div className="video-section__copy">
          <p className="eyebrow">{video.eyebrow}</p>
          <h2>{video.heading}</h2>
          <p>{video.intro}</p>
          <a className="text-link text-link--light" href={video.cta_url} target="_blank" rel="noreferrer">
            {video.cta_label} <Arrow />
          </a>
        </div>
        <div className="video-frame">
          {video.embed_url && playing ? (
            <iframe
              src={video.embed_url}
              title={video.embed_title}
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button className="video-frame__poster" onClick={() => setPlaying(true)} aria-label={ui.video.play}>
              <img src={video.poster} alt={video.embed_title} loading="lazy" />
              <span className="play-button">▶</span>
              <strong>{ui.video.play}</strong>
            </button>
          )}
          <p>{video.caption}</p>
        </div>
      </div>
    </section>
  );
}

function News({ openPost }) {
  const { ui, language, localizeNews } = useI18n();
  const localizedPosts = localizeNews(newsPosts);
  return (
    <section className="news section-pad" id="news">
      <div className="section-shell">
        <SectionIntro
          eyebrow={ui.news.eyebrow}
          title={ui.news.title}
          copy={ui.news.copy}
        />
        <div className="news-grid">
          {localizedPosts.slice(0, 6).map((post, index) => (
            <article
              className={`news-card ${index === 0 ? "news-card--featured" : ""}`}
              key={post.slug}
              itemScope
              itemType="https://schema.org/NewsArticle"
            >
              <button className="news-card__image" onClick={() => openPost(post)} aria-label={`Read ${post.title}`}>
                <img src={newsImageSrc(post.image)} alt={post.image_alt || ""} loading="lazy" itemProp="image" />
              </button>
              <div className="news-card__copy">
                <p className="news-card__meta"><span>{post.category}</span><time dateTime={post.date} itemProp="datePublished">{formatDate(post.date, language)}</time></p>
                <h3 itemProp="headline">{post.title}</h3>
                <p itemProp="description">{post.excerpt}</p>
                <meta itemProp="articleBody" content={post.body || post.excerpt} />
                {post.cta_url ? (
                  <a className="text-link" href={post.cta_url} target="_blank" rel="noreferrer">
                    {post.cta_label || ui.news.read} <Arrow />
                  </a>
                ) : (
                  <button className="text-link" onClick={() => openPost(post)}>{ui.news.read} <Arrow /></button>
                )}
              </div>
            </article>
          ))}
        </div>
        {localizedPosts.length > 6 && (
          <details className="news-archive">
            <summary className="button button--dark news-archive__toggle">
              {ui.news.archive} <span aria-hidden="true">+</span>
            </summary>
            <div className="news-archive__list">
              {localizedPosts.slice(6).map((post) => (
                <article className="news-archive__item" key={post.slug} itemScope itemType="https://schema.org/NewsArticle">
                  <img className="news-archive__image" src={newsImageSrc(post.image)} alt={post.image_alt || ""} loading="lazy" itemProp="image" />
                  <div className="news-archive__copy">
                    <p className="news-card__meta"><span>{post.category}</span><time dateTime={post.date} itemProp="datePublished">{formatDate(post.date, language)}</time></p>
                    <h3 itemProp="headline">{post.title}</h3>
                    <p itemProp="description">{post.excerpt}</p>
                    <details className="news-archive__article">
                      <summary>{ui.news.read} <Arrow /></summary>
                      <div itemProp="articleBody">
                        {(post.body || post.excerpt).split("\n").filter(Boolean).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                        {(post.contact_email || post.contact_whatsapp_url) && (
                          <p className="news-archive__contact">
                            <strong>{language === "de" ? "Kontakt und Reservation" : "Contact and registration"}</strong><br />
                            {post.contact_email && <><a href={`mailto:${post.contact_email}`}>{post.contact_email}</a><br /></>}
                            {post.contact_whatsapp_url && (
                              <a href={post.contact_whatsapp_url} target="_blank" rel="noreferrer">
                                {language === "de" ? "Telefon/WhatsApp" : "Phone/WhatsApp"}: {post.contact_phone}
                              </a>
                            )}
                          </p>
                        )}
                        {post.cta_url && (
                          <a className="text-link" href={post.cta_url} target="_blank" rel="noreferrer">
                            {post.cta_label || ui.news.read} <Arrow />
                          </a>
                        )}
                      </div>
                    </details>
                  </div>
                </article>
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}

function InstagramFeed() {
  const { content: { instagram: instagramSettings, site }, ui } = useI18n();
  const [posts, setPosts] = useState(instagramSettings.posts);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (instagramSettings.mode !== "auto") return;
    const controller = new AbortController();
    fetch("/api/instagram-feed", { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Instagram feed unavailable");
        return response.json();
      })
      .then((result) => {
        if (Array.isArray(result.posts) && result.posts.length) {
          setPosts(result.posts.slice(0, 6));
          setLive(true);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  return (
    <section className="instagram section-pad">
      <div className="section-shell">
        <SectionIntro
          eyebrow={live ? ui.instagram.live : ui.instagram.follow}
          title={instagramSettings.heading}
          copy={instagramSettings.intro}
          action={<a className="text-link" href={site.instagram_profile} target="_blank" rel="noreferrer"><InstagramIcon /> @tiagobjjdepaula <Arrow /></a>}
        />
        <div className="instagram-grid">
          {posts.slice(0, 6).map((post, index) => (
            <a className="instagram-card" href={post.permalink || site.instagram_profile} target="_blank" rel="noreferrer" key={`${post.permalink}-${index}`}>
              <img src={post.media_url || post.thumbnail_url || post.image} alt={post.caption || "Instagram post"} loading="lazy" />
              <span><InstagramIcon /> {ui.instagram.view}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrialForm() {
  const { content: { site }, ui, language } = useI18n();
  const [sent, setSent] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const subject = `${language === "de" ? "Anfrage Probetraining" : "Trial class request"} · ${data.get("dojo")}`;
    const body = [
      `Name: ${data.get("name")}`,
      `Email: ${data.get("email")}`,
      `Phone: ${data.get("phone") || "–"}`,
      `Dojo: ${data.get("dojo")}`,
      `${language === "de" ? "Interesse" : "Interested in"}: ${data.get("program")}`,
      "",
      data.get("message") || (language === "de" ? "Ich möchte ein Probetraining vereinbaren." : "I would like to arrange a trial class."),
    ].join("\n");
    if (["localhost", "127.0.0.1"].includes(window.location.hostname)) {
      setSent("mail");
      window.location.href = `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      return;
    }
    try {
      const payload = new URLSearchParams(data);
      payload.set("form-name", "trial-class");
      payload.set("language", language);
      const response = await fetch("/api/contact", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: payload.toString() });
      if (!response.ok) throw new Error("Form submission failed");
      setSent("success");
      form.reset();
    } catch {
      setSent("mail");
      window.location.href = `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  return (
    <section className="trial section-pad" id="trial">
      <div className="section-shell trial__grid">
        <div className="trial__copy">
          <p className="eyebrow">{site.contact.eyebrow}</p>
          <h2>{site.contact.title}</h2>
          <p>{site.contact.body}</p>
          <div className="trial__contacts">
            <a href={`tel:${site.phone.replace(/\s/g, "")}`}>{site.phone}</a>
            <a href={whatsappUrl(site.phone)} target="_blank" rel="noreferrer">WhatsApp</a>
            <a href={`mailto:${site.email}`}>{site.email}</a>
          </div>
        </div>
        <form className="trial-form" name="trial-class" method="POST" onSubmit={submit}>
          <div className="field-row">
            <label>{ui.form.name}<input type="text" name="name" autoComplete="name" required /></label>
            <label>{ui.form.email}<input type="email" name="email" autoComplete="email" required /></label>
          </div>
          <div className="field-row">
            <label>{ui.form.phone} <small>{ui.form.optional}</small><input type="tel" name="phone" autoComplete="tel" /></label>
            <label>{ui.form.dojo}<select name="dojo" defaultValue="Zurich Dojo"><option>Zurich Dojo</option><option>Silver Coast Dojo</option></select></label>
          </div>
          <label>{ui.form.program}
            <select name="program" defaultValue={language === "de" ? "Erwachsene – BJJ, Gi & No-Gi" : "Adult BJJ – Gi & No-Gi"}>
              <option>{language === "de" ? "Erwachsene – BJJ, Gi & No-Gi" : "Adult BJJ – Gi & No-Gi"}</option>
              <option>{language === "de" ? "Erwachsene – All Access" : "Adult All Access – BJJ, MMA & Striking"}</option>
              <option>{language === "de" ? "Jugendliche, Lernende & Studierende – All Access, 14–25 Jahre" : "Youth, Apprentices & Students – All Access, Ages 14–25"}</option>
              <option>{language === "de" ? "Kids BJJ – 1–2 Trainings pro Woche" : "Kids BJJ – 1–2 Classes per Week"}</option>
              <option>Kids BJJ Unlimited</option>
              <option>Women’s BJJ</option>
              <option>{language === "de" ? "MMA & Striking" : "MMA & Striking Only"}</option>
              <option>{language === "de" ? "Privatlektionen" : "Private Lessons"}</option>
              <option>Guest Day Pass</option>
            </select>
          </label>
          <label>{ui.form.message} <small>{ui.form.optional}</small><textarea name="message" rows="3" placeholder={ui.form.placeholder} /></label>
          <button className="button button--dark" type="submit">{site.contact.button} <Arrow /></button>
          {sent && <p className="form-note">{sent === "success" ? ui.form.success : ui.form.mail}</p>}
        </form>
      </div>
    </section>
  );
}

function ContactMaps() {
  const { content: { site }, ui } = useI18n();
  return (
    <section className="contact-maps section-pad" id="contact">
      <div className="section-shell">
        <SectionIntro
          eyebrow={ui.contact.eyebrow}
          title={ui.contact.title}
          copy={ui.contact.copy}
          action={<a className="text-link" href={`mailto:${site.email}`}>{site.email} <Arrow /></a>}
        />
        <div className="map-grid">
          {site.dojos.map((dojo) => (
            <MapCard dojo={dojo} loadLabel={ui.contact.load_map} key={dojo.id}>
              <div className="map-card__copy">
                <div>
                  <p className="eyebrow">{dojo.city}</p>
                  <h3>{dojo.name}</h3>
                  <p>{dojo.address}</p>
                </div>
                <a className="text-link" href={dojo.map_url} target="_blank" rel="noreferrer">{ui.contact.directions} <Arrow /></a>
              </div>
            </MapCard>
          ))}
        </div>
        <div className="contact-strip">
          <span>{ui.contact.questions}</span>
          <a href={`tel:${site.phone.replace(/\s/g, "")}`}>{site.phone}</a>
          <a href={whatsappUrl(site.phone)} target="_blank" rel="noreferrer">WhatsApp</a>
          <a href={`mailto:${site.email}`}>{site.email}</a>
        </div>
      </div>
    </section>
  );
}

function MapCard({ dojo, loadLabel, children }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <article className="map-card">
      <div className="map-card__map">
        {loaded ? (
          <iframe
            src={dojo.map_embed_url}
            title={`Map for ${dojo.name}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        ) : (
          <button className="map-card__loader" onClick={() => setLoaded(true)}>
            <img src={dojo.image} alt="" loading="lazy" />
            <span><PinIcon /> {loadLabel}</span>
          </button>
        )}
      </div>
      {children}
    </article>
  );
}

function FAQ() {
  const { language, ui } = useI18n();
  return (
    <section className="faq section-pad" id="faq">
      <div className="section-shell">
        <SectionIntro eyebrow={ui.faq.eyebrow} title={ui.faq.title} copy={ui.faq.copy} />
        <div className="faq__list">
          {faq.items.map((item, index) => (
            <details key={item.question_en} open={index === 0}>
              <summary><span>{String(index + 1).padStart(2, "0")}</span>{language === "de" ? item.question_de : item.question_en}<strong>+</strong></summary>
              <p>{language === "de" ? item.answer_de : item.answer_en}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Footer({ openLegal, homeLinks = false }) {
  const { content: { site }, ui, language } = useI18n();
  const home = homeLinks ? `/${language}/` : "";
  const partnerDojo = site.dojos.find((dojo) => dojo.partner_logo);
  return (
    <footer className="footer">
      <div className="section-shell footer__top">
        <Logo compact homeHref={homeLinks ? `/${language}/` : "#top"} />
        {partnerDojo && (
          <div className="footer__partner">
            <img src={partnerDojo.partner_logo} alt={partnerDojo.partner_logo_alt || partnerDojo.partner_name || "Partner logo"} loading="lazy" />
            <div><strong>{partnerDojo.partner_name || partnerDojo.partner_logo_alt}</strong><span>MMA</span></div>
          </div>
        )}
        <div className="footer__locations">
          {site.dojos.map((dojo) => (
            <div className="footer__location" key={dojo.id}>
              <div className="footer__location-copy"><strong>{dojo.name}</strong><span>{dojo.address}</span></div>
            </div>
          ))}
        </div>
        <div className="footer__links">
          <a href={site.instagram_profile} target="_blank" rel="noreferrer"><InstagramIcon /> {ui.footer.instagram}</a>
          <a href={`${home}#corporate`}>{language === "de" ? "Für Firmen" : "Corporate"}</a>
          <a href={`${home}#schedule`}>{ui.footer.schedule}</a>
          <a href={`${home}#trial`}>{ui.footer.trial}</a>
          <a href={site.member_portal} target="_blank" rel="noreferrer">{ui.footer.member}</a>
        </div>
      </div>
      <div className="section-shell footer__bottom">
        <span>
          © {new Date().getFullYear()} Tiago de Paula BJJ
          {" · "}{language === "de" ? "Entwickelt von" : "Developed by"} <a href="https://codelapa.com" target="_blank" rel="noreferrer">Codelapa</a>
          {" · "}{language === "de" ? "Konzept und Design von" : "Concept and design by"} <a href="https://www.linkedin.com/in/maurantoniom/" target="_blank" rel="noreferrer">Michael Maurantonio</a>
        </span>
        <nav className="footer__legal" aria-label={language === "de" ? "Rechtliche Informationen" : "Legal information"}>
          <a href="/documents/dojo-rules.pdf" target="_blank" rel="noreferrer">{ui.footer.rules}</a>
          {legal.documents.map((document) => (
            <a href={`#${document.id}`} onClick={(event) => { event.preventDefault(); openLegal(document.id); }} key={document.id}>
              {language === "de" ? document.title_de : document.title_en}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export function LegalModal({ documentId, close }) {
  const { language } = useI18n();
  const legalDocument = legal.documents.find((item) => item.id === documentId);

  useEffect(() => {
    if (!legalDocument) return undefined;
    const onKey = (event) => event.key === "Escape" && close();
    window.document.body.classList.add("modal-open");
    window.document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("modal-open");
      window.document.removeEventListener("keydown", onKey);
    };
  }, [legalDocument, close]);

  if (!legalDocument) return null;
  const isGerman = language === "de";
  return (
    <div className="modal legal-modal" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <article className="legal-modal__panel">
        <button className="modal__close" onClick={close} aria-label={isGerman ? "Rechtlichen Hinweis schliessen" : "Close legal notice"}>×</button>
        <header>
          <p className="eyebrow">TIAGO DE PAULA BJJ</p>
          <h2 id="legal-modal-title">{isGerman ? legalDocument.title_de : legalDocument.title_en}</h2>
          <p>{isGerman ? "Stand" : "Last updated"}: {formatDate(legalDocument.updated, language)}</p>
        </header>
        <div className="legal-modal__content">
          {legalDocument.sections.map((section) => (
            <section key={section.heading_en}>
              <h3>{isGerman ? section.heading_de : section.heading_en}</h3>
              <p>{isGerman ? section.body_de : section.body_en}</p>
            </section>
          ))}
        </div>
        <p className="legal-modal__note">{isGerman ? "Hinweis: Diese Texte bilden die aktuelle Website-Funktionalität ab und ersetzen keine individuelle Rechtsberatung." : "Note: These texts reflect the website’s current functionality and do not replace individual legal advice."}</p>
      </article>
    </div>
  );
}

function NewsModal({ post, close }) {
  const { ui, language } = useI18n();
  useEffect(() => {
    if (!post) return undefined;
    const onKey = (event) => event.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("modal-open");
    };
  }, [post, close]);

  if (!post) return null;
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <article className="modal__panel">
        <button className="modal__close" onClick={close} aria-label={ui.news.close}>×</button>
        <img src={newsImageSrc(post.image)} alt={post.image_alt || ""} />
        <div className="modal__copy">
          <p className="news-card__meta"><span>{post.category}</span><time>{formatDate(post.date, language)}</time></p>
          <h2 id="modal-title">{post.title}</h2>
          {post.body.split("\n").filter(Boolean).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {(post.contact_email || post.contact_whatsapp_url) && (
            <p>
              <strong>{language === "de" ? "Seminarreservation" : "Seminar registration"}</strong><br />
              {post.contact_email && <><a href={`mailto:${post.contact_email}`}>{post.contact_email}</a><br /></>}
              {post.contact_whatsapp_url && (
                <a href={post.contact_whatsapp_url} target="_blank" rel="noreferrer">
                  {language === "de" ? "Telefon/WhatsApp" : "Phone/WhatsApp"}: {post.contact_phone}
                </a>
              )}
            </p>
          )}
          {post.cta_url && (
            <div className="button-row">
              <a className="button" href={post.cta_url} target="_blank" rel="noreferrer">
                {post.cta_label || ui.news.read} <Arrow />
              </a>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}

function zurichDateKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function formatDate(value, language = "en") {
  // Date-only values use noon to avoid timezone shifts; ISO timestamps remain unchanged.
  const dateValue = /^\\d{4}-\\d{2}-\\d{2}$/.test(value) ? `${value}T12:00:00` : value;
  return new Intl.DateTimeFormat(language === "de" ? "de-CH" : "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(dateValue));
}

function annualSaving(plan, plans) {
  if (!/^12/.test(plan.duration)) return 0;
  const sixMonthPlan = plans.find((candidate) =>
    candidate.category === plan.category
    && candidate.name === plan.name
    && candidate.frequency === plan.frequency
    && /^6/.test(candidate.duration),
  );
  return sixMonthPlan ? Math.max(0, (Number(sixMonthPlan.price) * 2) - Number(plan.price)) : 0;
}

function initials(name) {
  return name.split(/\s+/).map((part) => part[0]).slice(0, 2).join("").toUpperCase();
}

function MobileTrialCta() {
  const { ui } = useI18n();
  return <a className="mobile-trial-cta" href="#trial">{ui.mobile_cta} <Arrow /></a>;
}

function whatsappUrl(phone) {
  return `https://wa.me/${phone.replace(/\D/g, "")}`;
}

function WhatsAppCta() {
  const { content: { site } } = useI18n();
  return (
    <a className="whatsapp-float" href={whatsappUrl(site.phone)} target="_blank" rel="noreferrer" aria-label="Open WhatsApp chat">
      WhatsApp
    </a>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [daypassEnabled, setDaypassEnabled] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/daypass/config', { signal: controller.signal }).then(response => response.ok ? response.json() : null).then(config => setDaypassEnabled(Boolean(config?.enabled && config.testMode === false))).catch(() => {});
    return () => controller.abort();
  }, []);
  const [dojo, setDojo] = useState("zurich");
  const [audience, setAudience] = useState("all");
  const [activePost, setActivePost] = useState(null);
  const [activeLegal, setActiveLegal] = useState(() => {
    if (typeof window === "undefined") return null;
    return legal.documents.some((item) => `#${item.id}` === window.location.hash)
      ? window.location.hash.slice(1)
      : null;
  });

  useEffect(() => {
    const syncLegalHash = () => {
      const id = window.location.hash.slice(1);
      setActiveLegal(legal.documents.some((item) => item.id === id) ? id : null);
    };
    window.addEventListener("hashchange", syncLegalHash);
    window.addEventListener("popstate", syncLegalHash);
    return () => {
      window.removeEventListener("hashchange", syncLegalHash);
      window.removeEventListener("popstate", syncLegalHash);
    };
  }, []);

  const openLegal = (id) => {
    window.history.pushState(null, "", `#${id}`);
    setActiveLegal(id);
  };

  const closeLegal = () => {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    setActiveLegal(null);
  };

  return (
    <div id="top">
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} daypassEnabled={daypassEnabled} />
      <main id="main">
        <Hero daypassEnabled={daypassEnabled} />
        <Intro />
        <Programs chooseAudience={setAudience} />
        <Corporate />
        <FirstVisit />
        <Dojos chooseDojo={setDojo} activeDojo={dojo} daypassEnabled={daypassEnabled} />
        <Schedule dojo={dojo} setDojo={setDojo} audience={audience} setAudience={setAudience} daypassEnabled={daypassEnabled} />
        <Pricing daypassEnabled={daypassEnabled} />
        <Coaches />
        <VideoSection />
        <News openPost={setActivePost} />
        <InstagramFeed />
        <ContactMaps />
        <FAQ />
        <TrialForm />
      </main>
      <Footer openLegal={openLegal} />
      <NewsModal post={activePost} close={() => setActivePost(null)} />
      <LegalModal documentId={activeLegal} close={closeLegal} />
      <WhatsAppCta />
      <MobileTrialCta />
    </div>
  );
}
